import React, { useRef, useEffect, FC, RefObject } from 'react';
import Transition from 'utils/Transition';
import {t} from "i18next";
import classNames from "classnames";

interface SlidingContainerProps {
  id: string;
  title: string;
  modalOpen: boolean;
  toCloseModal: (e:any) => void;
  toCloseModalOnClickOutside: (e:any) => void;
  modalContent: RefObject<HTMLDivElement>;
  children: any;
  minWidth?: number
}

export const SlidingContainer: FC<SlidingContainerProps> = ({
  id,
  title,
  modalOpen,
  toCloseModal,
  toCloseModalOnClickOutside,
  modalContent,
  children,
  minWidth
}) => {
  // close on click outside
  useEffect(() => {
    if (!modalOpen) return
    const clickHandler = (e: MouseEvent) => {
      if (modalOpen && !e.composedPath()?.includes(modalContent.current as HTMLDivElement)) {
        toCloseModalOnClickOutside(e);
      }
    };
    document.addEventListener('mousedown', clickHandler);
    return () => document.removeEventListener('mousedown', clickHandler);
  }, [modalOpen]);

  // close if the esc key is pressed
  useEffect(() => {
    const keyHandler = (e: KeyboardEvent) => {
      if (e.code !== '27') return;
      toCloseModalOnClickOutside(e);
    };
    document.addEventListener('keydown', keyHandler);
    return () => document.removeEventListener('keydown', keyHandler);
  });

  return (
    <>
      {/* Modal backdrop */}
      {/*@ts-ignore*/}
      <Transition
        className="fixed inset-0 bg-gray-900 bg-opacity-30 z-[70] transition-opacity"
        show={modalOpen}
        enter="transition ease-out duration-200"
        enterStart="opacity-0"
        enterEnd="opacity-100"
        leave="transition ease-out duration-100"
        leaveStart="opacity-100"
        leaveEnd="opacity-0"
        aria-hidden="true"
        appear={undefined}
      />
      {/* Modal dialog */}
      {/* @ts-ignore */}
      <Transition
        className="fixed inset-0 z-[70] overflow-hidden flex items-center justify-end transform"
        role="dialog"
        aria-modal="true"
        show={modalOpen}
        enter="transition ease-in-out duration-200"
        enterStart="opacity-0 translate-y-4"
        enterEnd="opacity-100 translate-y-0"
        leave="transition ease-in-out duration-200"
        leaveStart="opacity-100 translate-y-0"
        leaveEnd="opacity-0 translate-y-4"
      >
        <div
            id={id}
            className={classNames(
                `bg-white px-3 sm:px-8 rounded h-app m-0 shadow-lg absolute pb-16 sm:min-w-[511px]`,
                !minWidth ? 'sm:w-1/3' : ''
            )}
            style={{width:minWidth}}
            ref={modalContent}
        >
          <div className="bg-white z-10 sm:px-4 py-3 sticky top-0 ">
            <div className="flex justify-between items-center ">
              <div className="text-[#1E293B] font-bold text-xl">
                {t<string>(`${title}`, { ns: 'messages' })}
              </div>
              <div
                  className="text-gray-400 hover:text-gray-500 bg-[#F1F5F9] p-2 rounded-full cursor-pointer"
                  onClick={e => toCloseModal(e)}
              >
                <div className="sr-only">Close</div>
                <svg className="w-4 h-4 fill-current">
                  <path d="M7.95 6.536l4.242-4.243a1 1 0 111.415 1.414L9.364 7.95l4.243 4.242a1 1 0 11-1.415 1.415L7.95 9.364l-4.243 4.243a1 1 0 01-1.414-1.415L6.536 7.95 2.293 3.707a1 1 0 011.414-1.414L7.95 6.536z" />
                </svg>
              </div>
            </div>
          </div>
          <div className="py-4">
            <div className={'sm:px-4 h-[84vh] max-h-[84vh] overflow-y-auto'}>
              {children}
            </div>
          </div>
        </div>
      </Transition>
    </>
  );
};
